import React, { useCallback, useContext, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";

import {
  validateNewModule,
  validateNewModuleField,
  hasError,
  firstError,
} from "../moduleValidation";
import MediaDialog from "../../common/MediaDialog.js";
import EditColumn from "../../common/EditColumn";
import NewThumbnailOverlay from "../../common/NewThumbnailOverlay";
import { useSnackAlert } from "../../../common/use-snackAlert";
import {
  useModuleCreate,
  useUploadModuleThumbnail,
  useSuggestInternalCode,
} from "../moduleServices";
import { useRouteMatch } from "react-router-dom";
import { MediaContext } from "../../common/media-context";
import { allTrue, isBlank, notBlank } from "../../../util/ext";
import { useIsAdmin } from "../../../auth/authUtils";
import LoadingIndicator from "../../../components/LoadingIndicator";
import EditDescriptionColumn from "../../common/EditDescriptionColumn";
import OrganizationAutoComplete from "../../../user-admin/OrganizationAutoComplete";
import SaveButton from "../../../components/SaveButton";
import { lensProp, mergeLeft, mergeRight, omit, pipe, set } from "ramda";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Input,
  InputAdornment,
  InputLabel,
  Switch,
  Typography,
} from "@material-ui/core";
import SubscriptionTypeSelect from "../../../manage-subscriptions/SubscriptionTypeSelect";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import LuxonUtils from "@date-io/luxon";
import { useSubscriptionCreate } from "../../../manage-subscriptions/subscriptionService";

const useStyles = makeStyles(theme => ({
  root: {
    padding: "0",
  },

  close: {
    width: "40px",
    height: "40px",
    position: "relative",
    right: "-10px",
  },

  dialog: {
    width: "866px",
    borderRadius: "20px",
    boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.15)",
  },

  error: {
    color: theme.palette.error.main,
    paddingTop: 5,
  },

  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },

  title: {
    textAlign: "left",
    color: theme.palette.brandPrimary,
  },

  loadingIndicator: {
    position: "absolute",
    height: "100%",
    width: "100%",
    backgroundColor: theme.palette.secondary.dark,
    opacity: 0.7,
  },
}));

const validator = {
  validateField: validateNewModuleField,
  validate: validateNewModule,
  hasError: hasError,
  firstError: firstError,
};

const initialModule = {
  name: "",
  mediaType: "module",
  thumbnailUrl: null,
  status: "INACTIVE",
  description: "",
};

const PERPETUAL_END_DATE = "4000-01-01T00:00:00Z";

const initialSubscription = {
  type: "REGULAR",
  startDate: null,
  endDate: null,
  salesPrice: 0.0,
  perpetual: false,
  required: false,
};

const AddNewModuleDialog = props => {
  const classes = useStyles();
  const { path } = useRouteMatch();
  const context = useContext(MediaContext);
  const { open, onSave, onClose } = props;

  const isAdmin = useIsAdmin();

  const { SnackAlert, showAlert } = useSnackAlert();

  //-- Set up overall state
  const [module, setModule] = useState(initialModule);
  // for admin, pass org-id to POST /admin/modules to add principal to MAC table
  const [orgId, setOrgId] = useState(null);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [subscription, setSubscription] = useState(initialSubscription);

  const onSubscriptionChanged = useCallback(
    field => value => {
      const val = value && value.target ? value.target.value : value;
      const update = set(lensProp(field), val, subscription);
      setSubscription(update);
    },
    [subscription],
  );

  const onPerpetualChanged = useCallback(
    event => {
      const perpetual = event.target.checked;
      const definiteEnd = notBlank(subscription) ? subscription.endDate : null;
      const endDate = perpetual ? PERPETUAL_END_DATE : definiteEnd;

      setSubscription(mergeLeft({ perpetual, endDate }));
    },
    [subscription],
  );

  const uploadThumbnail = useUploadModuleThumbnail();
  const createInternalCode = useSuggestInternalCode();

  const reset = useCallback(() => {
    setModule(initialModule);
    setOrgId(null);
    setThumbnailUrl(null);
    setThumbnailFile(null);
    setErrors({});
  }, []);

  const validateSubscription = useCallback(() => {
    let errors = {};
    if (isAdmin) {
      // Admin must select an org for the MAC table
      if (isBlank(orgId)) {
        errors["orgId"] = ["An organization is required"];
      }

      if (subscription.required) {
        if (isBlank(subscription.startDate)) {
          errors["startDate"] = ["A start date is required for a subscription"];
        }

        if (isBlank(subscription.endDate)) {
          errors["endDate"] = ["An end date is required for a subscription"];
        }
      }
    }

    return errors;
  }, [isAdmin, orgId, subscription]);

  const createModule = useModuleCreate();
  const createSubscription = useSubscriptionCreate();
  const handleSave = useCallback(async () => {
    setLoading(true);

    const validation = pipe(
      mergeRight(validateNewModule(module)),
      mergeRight(validateSubscription()),
    )(errors);

    try {
      if (notBlank(validation) && isBlank(thumbnailFile)) {
        if (isBlank(thumbnailFile)) {
          setErrors(
            mergeRight(validation, {
              thumbnail: ["A thumbnail must be selected"],
            }),
          );
        } else {
          setErrors(validation);
        }

        throw Error("Please address missing or invalid values");
      }

      // Get suggested internal code
      const suggestedInternalCode = await createInternalCode.mutateAsync({
        data: { internalCode: module.name },
      });

      // Create new module
      module.internalCode = suggestedInternalCode.internalCode;
      module.displayName = module.name;

      const newModule = await createModule.mutateAsync({
        resourceIds: { orgId: isAdmin ? orgId : null },
        data: module,
      });

      // Upload thumbnail
      await uploadThumbnail.mutateAsync({
        resourceIds: { id: newModule.id },
        data: thumbnailFile,
      });

      // create subscription
      if (allTrue([isAdmin, subscription.required])) {
        await createSubscription.mutateAsync({
          data: {
            ...omit(["required"], subscription),
            organizationId: orgId,
            mediaId: newModule.id,
          },
        });
      }

      setLoading(false);
      onSave({
        redirectPath: path,
        newModuleId: newModule.id,
        mediaType: "module",
      });

      reset();
    } catch (error) {
      showAlert("error", [error.message]);
    } finally {
      setLoading(false);
    }
  }, [
    createModule,
    createSubscription,
    createInternalCode,
    errors,
    isAdmin,
    module,
    onSave,
    orgId,
    path,
    reset,
    showAlert,
    subscription,
    thumbnailFile,
    uploadThumbnail,
    validateSubscription,
  ]);

  const handleClose = useCallback(() => {
    reset();
    setLoading(false);
    onClose();
  }, [onClose, reset, setLoading]);

  const formBuilder = useCallback(
    handlers => {
      return (
        <MediaDialog
          open={open}
          maxWidth="md"
          fullWidth
          title="Add New Module"
          handleClose={handleClose}
          content={
            <Grid container>
              {loading && <LoadingIndicator transparent />}
              <Grid item style={{ marginRight: "16px" }}>
                <NewThumbnailOverlay
                  accept="jpg, jpeg, png, image/jpeg, image/jpg, image/png"
                  showAlert={showAlert}
                />
                {validator.hasError(errors, "thumbnail") && (
                  <div className={classes.error}>
                    {validator.firstError(errors, "thumbnail")}
                  </div>
                )}
              </Grid>
              <Grid item container xs>
                <Grid item xs={12}>
                  <TextField
                    id="module-name"
                    required
                    fullWidth
                    label="Title"
                    value={context.media.name}
                    onChange={handlers.onTextChange(["name"])}
                    error={hasError(errors, "name")}
                    helperText={firstError(errors, "name")}
                    onBlur={() => {
                      setErrors(validateNewModuleField("name", module.name));
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    id="module-subname"
                    fullWidth
                    label="Subtitle"
                    value={context.media.subName}
                    onChange={handlers.onTextChange(["subName"])}
                    error={hasError(errors, "subName")}
                    helperText={firstError(errors, "subName")}
                    onBlur={() => {
                      setErrors(
                        validateNewModuleField("subName", module.subName),
                      );
                    }}
                  />
                </Grid>
                {isAdmin && (
                  <Grid item xs={12} style={{ margin: "15px 0" }}>
                    <OrganizationAutoComplete
                      required
                      onChange={setOrgId}
                      onBlur={() => {
                        if (orgId) {
                          setErrors(omit(["orgId"]));
                        } else {
                          setErrors(
                            set(lensProp("orgId"), [
                              "An organization is required",
                            ]),
                          );
                        }
                      }}
                      helperText={firstError(errors, "orgId")}
                      error={firstError(errors, "orgId")}
                    />
                  </Grid>
                )}
              </Grid>

              {isAdmin && (
                <MuiPickersUtilsProvider utils={LuxonUtils}>
                  <Grid container item spacing={5}>
                    <Grid item xs={12} style={{ paddingBottom: 0 }}>
                      <Box mt={5}>
                        <Typography
                          style={{
                            color: "black",
                            fontWeight: "bolder",
                            fontSize: "2rem",
                            lineHeight: "2.6rem",
                          }}
                        >
                          Subscription
                        </Typography>
                      </Box>
                      <Typography variant="h5">
                        Add a subscription for an ACTIVE or INACTIVE module
                      </Typography>
                    </Grid>

                    <Grid container item xs={12}>
                      <Grid
                        component="label"
                        container
                        style={{ padding: 0, marginBottom: -20, width: "auto" }}
                      >
                        <Grid
                          item
                          style={{
                            position: "relative",
                            color: "rgba(0, 0, 0, 0.54)",
                            top: 10,
                            fontSize: "1.4rem",
                          }}
                        >
                          Module Status
                        </Grid>
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item>Inactive</Grid>
                          <Grid item>
                            <Switch
                              color="primary"
                              checked={module.status === "ACTIVE"}
                              onChange={event => {
                                const checked = event.target.checked;
                                setModule(
                                  set(
                                    lensProp("status"),
                                    checked ? "ACTIVE" : "INACTIVE",
                                  ),
                                );

                                if (!checked) {
                                  setErrors(omit(["startDate", "endDate"]));
                                }
                              }}
                            />
                          </Grid>
                          <Grid item>Active</Grid>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid container item xs={12}>
                      <Grid
                        component="label"
                        container
                        style={{ padding: 0, marginBottom: -20, width: "auto" }}
                      >
                        <Grid
                          item
                          style={{
                            position: "relative",
                            color: "rgba(0, 0, 0, 0.54)",
                            top: 10,
                            fontSize: "1.4rem",
                          }}
                        >
                          Create Subscription?
                        </Grid>
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item>No</Grid>
                          <Grid item>
                            <Switch
                              color="primary"
                              checked={subscription.required}
                              onChange={event => {
                                const checked = event.target.checked;
                                setSubscription(
                                  set(lensProp("required"), checked),
                                );
                              }}
                            />
                          </Grid>
                          <Grid item>Yes</Grid>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item sm={6} style={{ paddingTop: 0 }}>
                      <KeyboardDatePicker
                        disabled={!subscription.required}
                        required={subscription.required}
                        disableToolbar
                        fullWidth
                        autoOk
                        id="startDate"
                        format="MM/dd/yyyy"
                        label="Start Date"
                        variant="inline"
                        value={subscription.startDate}
                        onChange={onSubscriptionChanged("startDate")}
                        onBlur={() => {
                          if (subscription.startDate) {
                            setErrors(omit(["startDate"]));
                          } else {
                            setErrors(
                              set(lensProp("startDate"), [
                                "A start date is required for a subscription",
                              ]),
                            );
                          }
                        }}
                        helperText={firstError(errors, "startDate")}
                        error={firstError(errors, "startDate")}
                        KeyboardButtonProps={{
                          "aria-label": "change start date",
                        }}
                      />
                    </Grid>

                    <Grid item sm={6} style={{ paddingTop: 0 }}>
                      {!subscription.perpetual && (
                        <KeyboardDatePicker
                          disabled={!subscription.required}
                          required={subscription.required}
                          disableToolbar
                          fullWidth
                          autoOk
                          id="endDate"
                          format="MM/dd/yyyy"
                          label="End Date"
                          variant="inline"
                          maxDate="01/01/6000"
                          value={subscription.endDate}
                          onChange={onSubscriptionChanged("endDate")}
                          onBlur={() => {
                            if (subscription.endDate) {
                              setErrors(omit(["endDate"]));
                            } else {
                              setErrors(
                                set(lensProp("endDate"), [
                                  "An end date is required for an active module",
                                ]),
                              );
                            }
                          }}
                          helperText={firstError(errors, "endDate")}
                          error={firstError(errors, "endDate")}
                          KeyboardButtonProps={{
                            "aria-label": "change end date",
                          }}
                        />
                      )}
                    </Grid>

                    <Grid item sm={6}>
                      <SubscriptionTypeSelect
                        disabled={!subscription.required}
                        required={subscription.required}
                        value={subscription.type}
                        label="Subscription Type"
                        onChange={onSubscriptionChanged("type")}
                      />
                    </Grid>

                    <Grid item sm={6}>
                      <FormControl fullWidth>
                        <InputLabel htmlFor="salesPrice">
                          Sales Price
                        </InputLabel>
                        <Input
                          disabled={!subscription.required}
                          id="salesPrice"
                          value={subscription.salesPrice}
                          onChange={onSubscriptionChanged("salesPrice")}
                          startAdornment={
                            <InputAdornment position="start">$</InputAdornment>
                          }
                        />
                      </FormControl>
                    </Grid>

                    <Grid item sm={12}>
                      <FormControl fullWidth={true}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={!subscription.required}
                              checked={subscription.perpetual}
                              onChange={onPerpetualChanged}
                              name="perpetual"
                              color="primary"
                            />
                          }
                          label="Perpetual"
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </MuiPickersUtilsProvider>
              )}

              <Grid item xs={12} style={{ marginTop: 50 }}>
                <EditDescriptionColumn
                  required
                  enableLinks
                  allowHtml
                  error={validator.hasError(errors, "description")}
                  helperText={validator.firstError(errors, "description")}
                  onBlur={() =>
                    handlers.onBlur("description", module.description)
                  }
                />
              </Grid>
            </Grid>
          }
          actions={
            <SaveButton
              loading={loading}
              onClick={handleSave}
              disabled={loading}
            />
          }
        />
      );
    },
    [
      classes,
      context,
      errors,
      handleClose,
      handleSave,
      isAdmin,
      loading,
      module,
      onPerpetualChanged,
      onSubscriptionChanged,
      open,
      orgId,
      showAlert,
      subscription,
    ],
  );

  return (
    <MediaContext.Provider
      value={{
        media: module,
        setMedia: setModule,
        errors: errors,
        setErrors: setErrors,
        thumbnailUrl: thumbnailUrl,
        setThumbnailUrl: setThumbnailUrl,
        thumbnailFile: thumbnailFile,
        setThumbnailFile: setThumbnailFile,
      }}
    >
      <SnackAlert />
      <EditColumn
        isNew
        className={classes.root}
        formBuilder={formBuilder}
        validator={validator}
      />
    </MediaContext.Provider>
  );
};

export default AddNewModuleDialog;
